import Content from 'engage/components/Content/Content';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Calendar,
    Views,
    dateFnsLocalizer,
} from 'react-big-calendar'
import styles from './ContentCalendar.module.scss';
import cx from 'classnames';
import { useUIContext } from 'engage/context/UIContext';
import * as dates from '../../utils/index';
import CustomCalendarToolbar from '../CustomCalendarToolbar/CustomCalendarToolbar';
import { format, parse, startOfWeek, getDay, setHours, setMinutes } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import de from "date-fns/locale/de";
import nl from "date-fns/locale/nl"
import { useTranslation } from 'react-i18next';
import { capitalizeString } from 'engage/utils/stringHelper';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import SearchComponentWithTags from 'engage/components/SearchComponentWithTags/SearchComponentWithTags';
import VideoService from 'engage/service/VideoService';
import ContainerWithScroll from 'engage/components/ContainerWithScroll/ContainerWithScroll';
import { formatSecondsToDuration } from 'engage/utils/TimeUtil';
import { v4 as uuidv4 } from 'uuid';
import StorySchedule from 'engage/features/Storyboard/components/StorySchedule/StorySchedule';
import { getMsTime, getTodaysMinTime } from 'engage/features/Storyboard/utils';
import { ToastNotification } from 'engage/components/Toast/Toast';
import PageHeader from 'engage/components/PageHeader/PageHeader';
import useFetchListWithInfiniteScrollV2 from 'engage/hooks/useFetchListWithInfiniteScrollV2';
import Highlighter from 'react-highlight-words';
import useOutsideClick from 'engage/hooks/useOutsideClick';
import ScheduledStoryInfo from '../ScheduledStoryInfo/ScheduledStoryInfo';
import { useUserContext } from 'engage/context/UserContext';
import RecordNotFound from 'engage/components/_empty_state/RecordNotFound';
import TagsList from 'engage/routes/CompanySettings/components/Tags/TagsList/TagsList';
import Icon from 'engage/components/Icon/Icon';
import { CustomAgendaEvent, CustomAgendaTime, CustomEvent, CustomDayEvent } from '../CustomEvents';
import { millisecondsInHour } from 'date-fns/constants';

const INIT_MODAL_STYLES = { top: 0, left: 0 };

const MODAL_MARGIN = 8;

const DragAndDropCalendar = withDragAndDrop(Calendar)

const locales = { en: enGB, de, nl };

const ContentCalendar = ({ events, onSaveEvent, onRemoveEvent }) => {
    const [value, setValue] = useState('');
    const [eventToEdit, setEventToEdit] = useState(null);
    const [currentView, setCurrentView] = useState('month');
    const [eventModalStyle, setEventModalStyle] = useState(INIT_MODAL_STYLES);
    const [scheduledThisSession, setScheduledThisSession] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [draggedEvent, setDraggedEvent] = useState();
    const [selectedEvent, setSelectedEvent] = useState(null);

    const currentVideoCardRef = useRef();
    const editEventModalRef = useRef();
    const eventDetailsRef = useRef();
    const selectOtherEventRef = useRef();

    const { miniSidebar } = useUIContext();
    const { i18n, t } = useTranslation();

    const { user } = useUserContext();

    useOutsideClick(eventDetailsRef, () => {
        if (selectedEvent && !selectOtherEventRef.current) {
            setSelectedEvent(null);
            setEventModalStyle(INIT_MODAL_STYLES);
        }
        selectOtherEventRef.current = false;
    })

    const getVideosWithStats = useCallback(async query => {
        try {
            query.scheduled = false;
            const response = await VideoService.getStoriesList(query);

            return new Promise(function (resolve) {
                return resolve(response);
            });
        } catch (err) {
            return new Promise(function (resolve, reject) {
                return reject(err);
            });
        }
    }, []);

    const localEvents = useMemo(() => {
        const parsedEvents = events?.map(event => {
            if (user?.username == event?.story?.author?.username || event?.story?.permissions?.includes('update')) {
                event.isDraggable = true;
            }
            return event;
        });
        if (eventToEdit) {
            if (eventToEdit?.new) {
                return [...parsedEvents.map(item => ({ ...item, isDraggable: false })), eventToEdit];
            } else {
                return parsedEvents.map(item => item?.id == eventToEdit.id ? { ...eventToEdit } : { ...item, isDraggable: false });
            }
        }
        return parsedEvents;
    }, [events, eventToEdit, user]);

    const {
        infiniteRef,
        listFilteredById: videos,
        isEmptyList: isVideosEmpty,
        loading,
        searchInput: searchInputVideos,
        query,
    } = useFetchListWithInfiniteScrollV2({
        service: getVideosWithStats,
        filterOutIds: scheduledThisSession,
        bottomMinOffset: -5,
        scrollContainer: 'overflow'
    });

    const onInputChange = useCallback(
        value => {
            infiniteRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
            if (typeof value === 'string') {
                setValue(prevState => ({ ...prevState, query: value }));
                searchInputVideos?.onChange('search', value);
            } else {
                setValue(prevState => ({ ...prevState, tags: value }));
                searchInputVideos?.onChange(
                    'tags',
                    value.map(item => item.id)
                );
            }
        },
        []
    );
    const handleClearInput = () => {
        setValue('');
        searchInputVideos?.onChange('');
    };

    const currentLocale = useMemo(() => locales[i18n.language] || enGB, [i18n.language]);

    const localizer = dateFnsLocalizer({
        format,
        parse,
        startOfWeek: () => startOfWeek(new Date(), { locale: currentLocale }),
        getDay,
        locales,
    });

    const messages = useMemo(() => ({
        today: t("schedule.calendar.tooltip_today"),
        previous: t("schedule.calendar.tooltip_previous"),
        next: t("onboarding.btn.next"),
        month: capitalizeString(t("general.month")),
        week: t("schedule.calendar.tooltip_week"),
        work_week: t("schedule.calendar.tooltip_work_week"),
        day: capitalizeString(t("general.day")),
        agenda: t("schedule.calendar.tooltip_agenda"),
        allDay: t("schedule.calendar.agenda_all_day"),
        noEventsInRange: t("schedule.calendar.agenda_no_events"),
        showMore: (count) => t("schedule.calendar.show_more", { count }),
    }), [t]);

    const { components, defaultDate, max, views } = useMemo(
        () => ({
            components: {
                toolbar: CustomCalendarToolbar,
                event: CustomEvent,
                agenda: {
                    time: CustomAgendaTime,
                    event: CustomAgendaEvent,
                },
                day: {
                    event: CustomDayEvent,
                }
            },
            defaultDate: new Date(Date.now()),
            max: dates.add(dates.endOf(new Date(2015, 17, 1), 'day'), -1, 'hours'),
            views: Object.keys(Views).map((k) => Views[k]),
        }),
        []
    )

    const eventPropGetter = useCallback(
        (event) => ({
            ...(event.isDraggable
                ? { className: 'isDraggable' }
                : { className: 'nonDraggable' }),
        }),
        []
    )

    const handleVideoDragStart = useCallback((event) => {
        setDraggedEvent(event);
        setSelectedEvent(null);
    }, [])

    const customOnDragOverFromOutside = useCallback(
        (dragEvent) => {
            // check for undroppable is specific to this example
            // and not part of API. This just demonstrates that
            // onDragOver can optionally be passed to conditionally
            // allow draggable items to be dropped on cal, based on
            // whether event.preventDefault is called
            if (draggedEvent !== 'undroppable') {
                dragEvent.preventDefault()
            }
        },
        [draggedEvent]
    )

    const dragFromOutsideItem = useCallback((...args) => {

    }, [draggedEvent]);

    const defineEventModalStyles = useCallback((event, ref) => {
        const styles = { opacity: 1 };
        const overlay = document.querySelector('.rbc-overlay');
        if (overlay) {
            const { left, right, top } = overlay.getBoundingClientRect();
            styles.left = right + MODAL_MARGIN * 2;
            if (styles.left + ref.current.offsetWidth + MODAL_MARGIN * 2 > window.innerWidth) {
                const positionBeforeOverlay = left - ref.current.offsetWidth - MODAL_MARGIN * 2;
                if (styles.left > MODAL_MARGIN * 2) {
                    styles.left = positionBeforeOverlay;
                } else {
                    styles.maxWidth = window.innerWidth - styles.left - MODAL_MARGIN / 2;
                }
            }
            styles.top = top;
            if (styles.top + ref.current.offsetHeight + MODAL_MARGIN > window.innerHeight) {
                styles.top = window.innerHeight - ref.current.offsetHeight - MODAL_MARGIN * 2;
            }
        } else {
            let eventWrappers = Array.from(document.querySelectorAll(`div[data-videoId="${event.story?.id}"]`));
            switch (currentView) {
                case 'day':
                    const calendar = document.querySelector('.rbc-time-content');
                    if (eventWrappers?.length) {
                        const { top, width, left } = eventWrappers[0].getBoundingClientRect();
                        styles.top = top;
                        delete styles.right;
                        styles.left = left + width + MODAL_MARGIN * 2;
                        if (styles.left + ref.current.offsetWidth + MODAL_MARGIN * 2 > window.innerWidth) {
                            const positionBeforeOverlay = left - ref.current.offsetWidth - MODAL_MARGIN * 2;
                            if (positionBeforeOverlay > MODAL_MARGIN * 2) {
                                styles.left = positionBeforeOverlay;
                            } else {
                                styles.maxWidth = window.innerWidth - calendar?.getBoundingClientRect().right - MODAL_MARGIN * 2;
                            }
                        } else {
                            styles.maxWidth = window.innerWidth - calendar?.getBoundingClientRect().right - MODAL_MARGIN * 2;
                        }
                    } else {
                        styles.top = calendar?.getBoundingClientRect().top;
                        styles.right = MODAL_MARGIN;
                        styles.maxWidth = window.innerWidth - calendar?.getBoundingClientRect().right - MODAL_MARGIN * 2;
                    }
                    if (styles.top + ref.current.offsetHeight + MODAL_MARGIN > window.innerHeight) {
                        styles.top = window.innerHeight - ref.current.offsetHeight - MODAL_MARGIN * 2;
                    }
                    break;
                case 'agenda':
                    styles.right = MODAL_MARGIN;
                    const agenda = document.querySelector('.rbc-agenda-content');
                    const agendaEventWrappers = Array.from(agenda.querySelectorAll(`[aria-event-id="${event.id}"]`));
                    const eventBoundaries = agendaEventWrappers[0].getBoundingClientRect();
                    styles.left = eventBoundaries.left + MODAL_MARGIN * 2;
                    styles.top = eventBoundaries.top + eventBoundaries.height + MODAL_MARGIN;
                    styles.maxWidth = window.innerWidth - agenda?.getBoundingClientRect().right - MODAL_MARGIN * 2;
                    if (styles.top + ref.current.offsetHeight + MODAL_MARGIN > window.innerHeight) {
                        styles.top = window.innerHeight - ref.current.offsetHeight - 2 * MODAL_MARGIN;
                    }
                    break;
                case 'week':
                case 'work_week':
                    const { top: firstTop, left: firstLeft, width: firstWidth } = eventWrappers[0].getBoundingClientRect();
                    styles.left = firstLeft + firstWidth + MODAL_MARGIN * 2;
                    styles.top = firstTop;
                    if (styles.left + ref.current.offsetWidth + MODAL_MARGIN * 2 > window.innerHeight || eventWrappers.length > 1) {
                        const positionBeforeOverlay = firstLeft - ref.current.offsetWidth - MODAL_MARGIN * 2;
                        if (positionBeforeOverlay < MODAL_MARGIN * 2) {
                            const { left, width, top } = eventWrappers[eventWrappers.length - 1].getBoundingClientRect();
                            styles.top = top;
                            styles.left = left + width + MODAL_MARGIN * 2;
                        } else {
                            styles.left = positionBeforeOverlay;
                        }
                    }
                    if (styles.left + ref.current.offsetWidth + MODAL_MARGIN * 2 > window.innerWidth) {
                        styles.maxWidth = window.innerWidth - styles.left - MODAL_MARGIN / 2;
                    }
                    if (styles.top + ref.current.offsetHeight + MODAL_MARGIN > window.innerHeight) {
                        styles.top -= (styles.top + ref.current.offsetHeight + MODAL_MARGIN) - window.innerHeight;
                    }
                    break;
                default:
                    const viewContainer = document.querySelector('div[aria-label= "Month View"]');
                    const viewRect = viewContainer?.getBoundingClientRect();
                    let boundingClientRect = {};
                    if (eventWrappers.length) {
                        boundingClientRect = eventWrappers[0].getBoundingClientRect();
                    } else {
                        const startDay = event.start.getDate();
                        const dateRows = Array.from(document.querySelectorAll('.rbc-button-link')).filter(button => button.textContent.trim() === `${startDay < 10 ? `0${startDay}` : `${startDay}`}`);
                        const currentMonth = currentDate.getMonth();
                        const eventMonth = event.start.getMonth();
                        if (currentMonth !== eventMonth) {
                            boundingClientRect = dateRows.find(item => Array.from(item.parentElement.classList).find(item => item.includes('off-range')))?.getBoundingClientRect();
                        } else {
                            boundingClientRect = dateRows.find(item => !Array.from(item.parentElement.classList).find(item => item.includes('off-range'))).getBoundingClientRect();
                        }
                    }
                    // Month view
                    const { top, left, width } = boundingClientRect;
                    styles.top = top - ref.current.offsetHeight / 2 - MODAL_MARGIN * 2;
                    if (styles.top + ref.current.offsetHeight > window.innerHeight) {
                        styles.top = window.innerHeight - ref.current.offsetHeight - MODAL_MARGIN * 2;
                    }
                    if (styles.top < 0) {
                        styles.top = viewRect?.top + ref.current.offsetHeight < window.innerHeight ? viewRect?.top : MODAL_MARGIN * 2;
                    } else if (styles.top < viewRect?.top) {
                        styles.top = viewRect?.top + ref.current.offsetHeight < window.innerHeight ? viewRect?.top : styles.top - MODAL_MARGIN * 2;
                    }
                    if (eventWrappers.length <= 1) {
                        // By default the modal is shown to the top left of the event (if there is enough space)
                        styles.left = left - ref.current.offsetWidth - MODAL_MARGIN * 2;
                        if (styles.left < 0) {
                            styles.left = left + width + MODAL_MARGIN * 2;
                            if (styles.left + ref.current.offsetWidth + MODAL_MARGIN * 2 > window.innerWidth) {
                                styles.maxWidth = window.innerWidth - styles.left - MODAL_MARGIN / 2;
                            }
                        }
                    } else {
                        // If there are more then 1 event, to not overlap event with modal, show modal on the left side
                        styles.right = MODAL_MARGIN;
                        styles.maxWidth = window.innerWidth - viewRect?.right - MODAL_MARGIN * 2;
                    }
            }
        }
        // For cases when event had only start date, but got an end date after resize, 
        // and the modal height changed because end date filed was added 
        setTimeout(() => {
            if (parseInt(styles.top) + ref.current.offsetHeight > window.innerHeight) {
                setEventModalStyle(prevState => ({ ...prevState, top: `${window.innerHeight - ref.current.offsetHeight - MODAL_MARGIN * 2}px` }));
            }
        }, 50)
        for (const key in styles) {
            if (key != 'transform' && key != 'opacity' && !(styles?.transform && key != 'top')) {
                styles[key] = `${Math.round(styles[key])}px`;
            }
        }
        setEventModalStyle(styles);
    }, [currentView, currentDate]);


    const onDropFromOutside = useCallback(
        ({ start, allDay: isAllDay }) => {
            if (draggedEvent === 'undroppable') {
                setDraggedEvent(null)
                return
            }

            let startDate = Date.now() > start.getTime() ? getTodaysMinTime() : (currentView == 'month' ? setMinutes(setHours(start, 9), 0) : start);

            const { title, videoId } = draggedEvent;
            const event = {
                id: uuidv4(),
                story: {
                    id: videoId,
                    name: title,
                },
                start: startDate,
                end: setHours(startDate, startDate.getHours() + 1), // Set time to +1 hour
                isAllDay,
                isDraggable: true,
                new: true,
                hasDeadline: false,
                notifications: [],
            }
            setDraggedEvent(null)
            setEventToEdit(event);
            const videoItem = infiniteRef.current.querySelector(`li[id="${videoId}"]`);
            if (videoItem) {
                currentVideoCardRef.current = videoItem;
                videoItem.style.filter = 'grayscale(100%)';
                videoItem.style.pointerEvents = 'none';
            }
            setTimeout(() => {
                defineEventModalStyles(event, editEventModalRef);
            }, 150)
        },
        [draggedEvent, setDraggedEvent, defineEventModalStyles, currentView]
    )

    const resizeEvent = useCallback(
        ({ event, start, end }) => {
            if (eventToEdit && event.id !== eventToEdit.id) {
                return;
            }
            let isStartChanged = false;

            if (eventToEdit) {
                isStartChanged = new Date(eventToEdit?.start).getTime() !== new Date(start).getTime();
            } else {
                const eventFromList = localEvents.find(item => item.id == event.id);
                isStartChanged = new Date(eventFromList?.start).getTime() !== new Date(start).getTime()
            }
            if (isStartChanged && getMsTime(start) < Date.now()) {
                ToastNotification({
                    message: t('storyboard.editor.schedule.start_date_error'),
                })
                return;
            }
            setEventToEdit((prevState) => ({
                ...(prevState ? prevState : { ...event, edited: true }),
                start,
                end: end.getTime() - start.getTime() < millisecondsInHour
                    ? setHours(setMinutes(start, 0), start.getHours() + 1) // Set time to +1 hour
                    : end,
                hasDeadline: end.getTime() - start.getTime() > millisecondsInHour,
            }));
            setSelectedEvent(null);
            editEventModalRef.current.style.display = 'flex';
            setTimeout(() => {
                defineEventModalStyles(event, editEventModalRef);
            }, 150)
        },
        [eventToEdit, defineEventModalStyles, localEvents]
    )

    const moveEvent = useCallback(
        ({ event, start, end, isAllDay: droppedOnAllDaySlot = false }) => {
            const { allDay } = event
            if (!allDay && droppedOnAllDaySlot) {
                event.allDay = true
            }
            if (allDay && !droppedOnAllDaySlot) {
                event.allDay = false;
            }
            if (eventToEdit && eventToEdit.id !== event.id) {
                return;
            }
            if (getMsTime(start) > Date.now()) {
                setEventToEdit(prevState => ({
                    ...(prevState ? prevState : { ...event, edited: true }),
                    start,
                    ...(end.getTime() - start.getTime() < millisecondsInHour ? {
                        // Also update end date
                        end: start.getHours() === 0 && start.getMinutes() === 0
                            ? setHours(setMinutes(start, 0), 18) // Set time to 6:00 PM
                            : start,
                    } : {
                        end: end.getHours() === 0 && end.getMinutes() === 0
                            ? setHours(setMinutes(end, 0), 9) // Set time to 6:00 PM
                            : end,
                        allDay: event.allDay
                    }),
                    hasDeadline: end.getTime() - start.getTime() > millisecondsInHour,
                }));
                editEventModalRef.current.style.display = 'flex';
                setSelectedEvent(null);
                setTimeout(() => {
                    defineEventModalStyles(event, editEventModalRef);
                }, 150)
            } else {
                ToastNotification({
                    message: t('storyboard.editor.schedule.start_date_error'),
                })
            }
        },
        [eventToEdit, defineEventModalStyles]
    )

    const handleEventDragStart = useCallback(() => {
        if (eventToEdit) {
            setEventModalStyle({ display: 'none', opacity: 0 });
        }
    }, [eventToEdit])

    const handleCurrentViewChange = useCallback((view) => {
        setCurrentView(view);
        setSelectedEvent(null);
        setEventModalStyle({ opacity: 0, maxWidth: 'unset' });
        if (eventToEdit) {
            setCurrentDate(new Date(eventToEdit.start));
        }
    }, [eventToEdit, defineEventModalStyles])

    const handleOptionChanged = useCallback(({ start = null, end = null, privacy = null }) => {
        if (start) {
            setEventToEdit((prevState) => ({ ...prevState, start: new Date(start), ...(prevState.end.getDate() !== prevState.start.getDate() || prevState.end.getMonth() !== prevState.start.getMonth() ? {} : { end: setHours(setMinutes(new Date(start), 0), 18) }) }));
            setTimeout(() => {
                defineEventModalStyles(eventToEdit, editEventModalRef);
            }, 150)
        }
        if (end) {
            setEventToEdit((prevState) => ({ ...prevState, end: new Date(end) }));
            setTimeout(() => {
                defineEventModalStyles(eventToEdit, editEventModalRef);
            }, 150)
        }
        if (privacy) {
            setEventToEdit((prevState) => ({ ...prevState, privacy }));
        }
    }, [eventToEdit, defineEventModalStyles])

    const handleCancelNewEvent = () => {
        setEventToEdit(null);
        setEventModalStyle(INIT_MODAL_STYLES);
        if (currentVideoCardRef.current) {
            currentVideoCardRef.current.style.filter = 'none';
            currentVideoCardRef.current.style.pointerEvents = 'all';
            currentVideoCardRef.current = null;
        }
    }

    const handleUpdateEvent = useCallback(async (start, end, notes, notifications) => {
        const payload = { notes };
        if (eventToEdit?.new) {
            payload.start = start;
            payload.end = end;
            payload.newNotifications = notifications;
        } else {
            const initialEvent = eventToEdit?.new ? {} : events.find(item => item.id == eventToEdit.id);
            const startTime = new Date(initialEvent.start).getTime();
            if (start !== startTime) {
                payload.start = start;
            }
            if (end !== null && end - start > millisecondsInHour) {
                if (initialEvent.end - initialEvent.start <= millisecondsInHour || end !== new Date(initialEvent.end).getTime()) {
                    payload.end = end;
                }
            } else {
                payload.end = null;
            }

            payload.newNotifications = notifications.filter(item => item.hasOwnProperty('tempId'));;
            payload.editedNotifications = notifications.filter(item => !item.hasOwnProperty('tempId')).map(item => {
                const oldNotification = initialEvent.notifications.find(oldItem => oldItem.id == item.id);
                if (oldNotification && oldNotification.offset !== item.offset) {
                    return true;
                }
                return false;
            });
        }
        setEventToEdit(prevState => ({ ...prevState, loading: true, isDraggable: false }));
        await onSaveEvent(eventToEdit.story.id, payload, eventToEdit?.new ?? false);
        setEventToEdit(null);
        setScheduledThisSession(prevState => [...prevState, eventToEdit.story.id]);
    }, [eventToEdit, events]);

    const handleRemoveEvent = useCallback(() => {
        onRemoveEvent(eventToEdit?.story?.id);
        setEventToEdit(null);
    }, [eventToEdit])

    const handleEventSelect = useCallback((event) => {
        if (event.id == eventToEdit?.id) {
            editEventModalRef.current.style.display = 'flex';
            setTimeout(() => {
                defineEventModalStyles(eventToEdit, editEventModalRef);
            }, 150)
        } else {
            selectOtherEventRef.current = selectedEvent !== null;
            setSelectedEvent(event);
            if (eventToEdit?.id) {
                setEventModalStyle(INIT_MODAL_STYLES);
                setEventToEdit(null);
            }
            setTimeout(() => {
                defineEventModalStyles(event, eventDetailsRef);
            }, 150)
        }
    }, [eventToEdit, selectedEvent, defineEventModalStyles]);

    const handleEditEvent = useCallback(() => {
        setEventToEdit(selectedEvent);
        setSelectedEvent(null);
        setEventModalStyle(null);
        setTimeout(() => {
            defineEventModalStyles(selectedEvent, editEventModalRef);
        }, 150)
    }, [selectedEvent, defineEventModalStyles]);

    const handleCloseEventDetails = useCallback(() => {
        setSelectedEvent(null);
        setEventModalStyle(INIT_MODAL_STYLES);
    }, []);

    useEffect(() => {
        if (eventToEdit != null) {
            setTimeout(() => {
                defineEventModalStyles(eventToEdit, editEventModalRef);
            }, 150)
        }
    }, [currentDate, currentView])

    return <Content className={cx(styles.editContent, { [styles.miniSidebar]: miniSidebar })}>
        <div className={styles.pageWrapper}>
            <div className={styles.calendarWrapper}>
                <div className={styles.container}>
                    <PageHeader title={t('schedule.title')} />
                    <DragAndDropCalendar
                        components={components}
                        defaultDate={defaultDate}
                        events={localEvents}
                        localizer={localizer}
                        max={max}
                        defaultView='month'
                        showMultiDayTimes
                        step={60}
                        views={views}
                        culture={i18n.language}
                        messages={messages}
                        style={{
                            gap: '16px',
                            border: 'none',
                            fontFamily: `'Nunito Sans', sans-serif'`
                        }}
                        onDragOverFromOutside={customOnDragOverFromOutside}
                        dragFromOutsideItem={dragFromOutsideItem}
                        onDropFromOutside={onDropFromOutside}
                        draggableAccessor="isDraggable"
                        eventPropGetter={eventPropGetter}
                        onSelectSlot={eventToEdit}
                        onEventResize={resizeEvent}
                        onEventDrop={moveEvent}
                        onDragStart={handleEventDragStart}
                        onView={handleCurrentViewChange}
                        date={currentDate}
                        popup={true}
                        onNavigate={(date) => setCurrentDate(date)}
                        onSelectEvent={handleEventSelect}
                    />
                </div>
            </div>
            <div className={styles.videosSidebar} ref={infiniteRef}>
                <h2 className={styles.title}>{t('schedule.sidebar.title')}</h2>
                <p className={styles.description}>{t('schedule.sidebar.description')}</p>
                <SearchComponentWithTags
                    placeholder={t('dashboard.searching.placeholder')}
                    alwaysShow={true}
                    onChange={onInputChange}
                    onClose={handleClearInput}
                    clearButton={true}
                    searchValue={value} />
                <ContainerWithScroll as='ul' ref={infiniteRef} className={styles.videoListContainer}>
                    {isVideosEmpty ? <div className={styles.emptyList}>
                        {!loading && (query?.search || query?.tags?.length > 0) && videos?.length === 0 && <RecordNotFound searchTerm={query.search} />}
                    </div> : videos?.map(video => {
                        return (
                            <li key={video.id} id={video.id} className={cx(styles.videoItem, { [styles.disabled]: eventToEdit !== null })} draggable={eventToEdit == null}
                                onDragStart={() =>
                                    handleVideoDragStart({ title: video.name, videoId: video.id, isDraggable: true })
                                }>
                                <Highlighter
                                    highlightClassName="wordHighlighter"
                                    searchWords={searchInputVideos?.value?.trim().length > 0 ? [searchInputVideos?.value] : ['']}
                                    autoEscape
                                    textToHighlight={video.name}
                                    className={styles.videoName}
                                />
                                <div className={styles.videoInfo}>
                                    <span className={styles.createdDate}>{t('general.date', { date: new Date(video.createdAt) })}</span>
                                    <span className={styles.draftOrDuration}>{video.isDraft ? t('dashboard.card_col.draft_status') : formatSecondsToDuration(video?.duration / 1000)}</span>
                                </div>
                                {video.orientation ? <div className={styles.orientationWrapper}>
                                    <Icon src={`${video.orientation}Storyboard`} className={styles.orientationIcon} />
                                    <span className={styles.name}>{t(`storyboard.generating.orientation.${video.orientation}`)}</span>
                                </div> : null}
                                {video?.tags?.length ? <ul className={styles.tagsList}><TagsList tags={video?.tags} size='small' /></ul> : null}
                            </li>
                        );
                    })}
                    {loading && <li key={'loading'} className={styles.loading}>{t('general.loading')}</li>}
                </ContainerWithScroll>
            </div>
            {eventToEdit !== null && <div className={styles.newEventModal} ref={editEventModalRef} style={eventModalStyle}>
                <StorySchedule
                    minimize={true}
                    onOptionChanged={handleOptionChanged}
                    storySchedule={{ ...eventToEdit, ...(eventToEdit?.hasDeadline ? {} : { end: null }) }}
                    onClose={handleCancelNewEvent}
                    onSubmit={handleUpdateEvent}
                    editing={eventToEdit?.new || eventToEdit?.isDraggable}
                    defaultNotification={eventToEdit?.new ?? false}
                    onRemove={handleRemoveEvent}
                    allowStartDateExpired={!eventToEdit?.new} />
            </div>}
            {selectedEvent !== null && <div ref={eventDetailsRef} className={styles.eventDetails} style={eventModalStyle}>
                <ScheduledStoryInfo story={selectedEvent?.story} handleEditEvent={handleEditEvent} onClose={handleCloseEventDetails} />
            </div>}
        </div>
    </Content>
}

export default ContentCalendar;